import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { formatPrice, formatHour, SPORT_COLORS, hexToRgba } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import BackButton from "@/components/BackButton";
import { Calendar, Clock, Users, Activity } from "lucide-react";

export default function MySessions() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sessions").then((r) => setSessions(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-10">
        <BackButton className="mb-8" />

        <div className="label-eyebrow text-volt mb-2">{t("play.eyebrow")}</div>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-8">{t("play.my_sessions_title")}</h1>

        {loading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="border border-border/30 bg-card p-5 animate-pulse">
                <div className="h-5 bg-muted w-1/2 mb-2" />
                <div className="h-4 bg-muted w-1/3" />
              </div>
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center">
            <Activity className="h-10 w-10 text-muted-foreground/40 mx-auto mb-4" />
            <p className="font-heading text-2xl text-muted-foreground/80">{t("play.no_sessions")}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => {
              const isHost = user?.id === s.host_id;
              const myClaim = s.claims?.find((c) => c.captain_id === user?.id);
              return (
                <Link
                  key={s.id}
                  to={`/play/${s.id}`}
                  className="block border border-border bg-card p-5 hover:border-volt/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-10 h-10 flex items-center justify-center shrink-0"
                        style={{ background: hexToRgba(SPORT_COLORS[s.sport]?.bg, 0.15) }}
                      >
                        <Activity className="h-5 w-5" style={{ color: SPORT_COLORS[s.sport]?.bg }} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-heading text-xl tracking-tight">{s.court_name}</div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                          <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {s.date}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatHour(s.start_hour)} – {formatHour(s.end_hour)}</span>
                          <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {s.filled_count}/{s.max_players}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {isHost && (
                        <span className="text-[9px] tracking-widest uppercase border border-yellow-400/30 px-1.5 py-0.5 text-yellow-400">
                          {t("play.host")}
                        </span>
                      )}
                      {myClaim && !isHost && (
                        <span className="text-[9px] tracking-widest uppercase border border-volt/30 px-1.5 py-0.5 text-volt">
                          {t("play.player")}
                        </span>
                      )}
                      {s.status === "confirmed" && (
                        <span className="text-[9px] tracking-widest uppercase border border-emerald-400/30 px-1.5 py-0.5 text-emerald-400">
                          {t("play.confirmed")}
                        </span>
                      )}
                      {s.status === "cancelled" && (
                        <span className="text-[9px] tracking-widest uppercase border border-red-400/30 px-1.5 py-0.5 text-red-400">
                          {t("play.cancelled")}
                        </span>
                      )}
                      {s.status === "open" && (
                        <span className="text-[9px] tracking-widest uppercase border border-border px-1.5 py-0.5 text-muted-foreground/80">
                          {t("play.open")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-muted-foreground">
                      {t("home.sports." + s.sport)}
                    </span>
                    <span className="text-heading text-lg text-volt">{formatPrice(s.price_per_player, s.currency)} {t("play.per_player_short")}</span>
                  </div>
                  {(() => {
                    const now = new Date();
                    const sessionEnd = new Date(s.date);
                    sessionEnd.setHours(s.end_hour || 0, 0, 0, 0);
                    const isPastSession = now > sessionEnd;
                    const isClaimant = s.claims?.some(c => c.captain_id === user?.id);
                    const isPlayable = s.status === "confirmed" || s.status === "completed";
                    if (!isPastSession || !isClaimant || isHost || !isPlayable) return null;
                    return (
                      <div className="mt-2">
                        <span
                          className="text-[10px] tracking-widest uppercase text-volt cursor-pointer hover:underline"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/play/${s.id}?rate=1`);
                          }}
                        >
                          {t("play.rate_players")} →
                        </span>
                      </div>
                    );
                  })()}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
